import React, { useState } from 'react';
import { withRouter } from 'react-router-dom';
import get from 'lodash/get';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PropTypes from 'prop-types';
import {
  localeFullDateFormat,
  localeTimeFormat,
  RunTaskStatuses,
  RunTaskTypes,
} from '../../../lib/utils';
import { RunTaskProgress } from './run-task-progress';
import { RunTask } from './run-task';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);

const styles = {
  header: {
    fontSize: '18px',
    marginTop: '12px',
  },
  subHeader: {
    color: 'grey',
  },
  title: {
    marginLeft: '12px',
    marginRight: '12px',
  },
  content: {
    marginLeft: '12px',
    marginRight: '12px',
    paddingBottom: '48px',
  },
  timeIcon: {
    color: '#4CAF50',
    marginTop: '8px',
    fontSize: '18px',
  },
  timeDisplay: {
    fontSize: '12px',
    marginLeft: '4px',
    marginRight: '12px',
  },
  progressContainer: {
    marginTop: '16px',
  },
  runTaskContainer: {
    maxHeight: '212px',
    overflowY: 'auto',
    backgroundColor: '#f6fafd',
    padding: '24px 24px 0 24px',
    marginTop: '20px',
  },
  flexRow: {
    display: 'flex',
  },
  selectContainer: {
    marginTop: '12px',
  },
  label: {
    color: 'grey',
    '&.MuiFocused': {
      color: 'grey',
    },
  },
  select: {
    '&.MuiFocused .MuiOutlinedInputNotchedOutline': {
      borderColor: 'grey',
    },
    height: '48px',
  },
};

function BaseRunTaskModal({
  handleClose, run, open, tz,
}) {
  const statusFilterTypes = {
    ALL: 'All',
    COMPLETED: 'Completed',
    NOT_COMPLETED: 'Not completed',
    ATTEMPTED: 'Attempted',
    COMPLETED_OUTSIDE: 'Completed outside of recommendation',
    ATTEMPTED_OUTSIDE: 'Attempted outside of recommendation',
  };
  const PURE_PICKUP = 'Pure Pickup';
  const [typeFilters, setTypeFilters] = useState([
    RunTaskTypes.DEPLOY,
    RunTaskTypes.PICKUP,
    PURE_PICKUP,
  ]);
  const [statusFilter, setStatusFilter] = useState(statusFilterTypes.ALL);

  const changeTypeFilter = (filter) => {
    if (typeFilters.includes(filter)) {
      setTypeFilters([...typeFilters.filter((item) => item !== filter)]);
    } else {
      typeFilters.push(filter);
      setTypeFilters([...typeFilters]);
    }
  };

  const showTask = (runTask, statusFilter, typeFilters) => {
    const hasAssociation = !!runTask.associatedRunTask;
    const isCompleted = runTask.status === RunTaskStatuses.COMPLETED;
    const isRecommended = runTask.isRecommended;
    const isDeploy = runTask.type === RunTaskTypes.DEPLOY;
    const isPickup = runTask.type === RunTaskTypes.PICKUP;
    const isPurePickup = isPickup && !hasAssociation && !isRecommended;
    const associatedRunTask = runTask.associatedRunTask;

    if (typeFilters.includes(RunTaskTypes.DEPLOY)) {
      // if it has an association, it is a rebalance of some kind
      if (isDeploy && !hasAssociation) {
        if (statusFilter === statusFilterTypes.ALL) {
          return true;
        } else if (statusFilter === statusFilterTypes.COMPLETED) {
          return isCompleted && isRecommended;
        } else if (statusFilter === statusFilterTypes.NOT_COMPLETED) {
          return runTask.status === RunTaskStatuses.OPEN && isRecommended;
        } else if (statusFilter === statusFilterTypes.COMPLETED_OUTSIDE) {
          return !isRecommended;
        } else { // attempted filters - pure deploys can never be attempted
          return false;
        }
      }
    }
    if (typeFilters.includes(RunTaskTypes.PICKUP)) { // rebalance
      if (hasAssociation || isRecommended) {
        if (statusFilter === statusFilterTypes.ALL) {
          return true;
        } else if (statusFilter === statusFilterTypes.COMPLETED) {
          if (isCompleted && isRecommended && hasAssociation && associatedRunTask.isRecommended) {
            return true;
          }
        } else if (statusFilter === statusFilterTypes.NOT_COMPLETED) {
          if (isPickup && isCompleted && !hasAssociation) {
            return true;
          }
          return runTask.status === RunTaskStatuses.OPEN;
        } else if (statusFilter === statusFilterTypes.ATTEMPTED) {
          return runTask.status === RunTaskStatuses.ATTEMPTED;
        } else if (statusFilter === statusFilterTypes.COMPLETED_OUTSIDE) {
          if (isDeploy) {
            if ((isRecommended && hasAssociation && !associatedRunTask.isRecommended)
                 || !isRecommended) {
              return true;
            }
          }
          // for pickup task
          if (hasAssociation && !isRecommended
              || (isRecommended && hasAssociation && !associatedRunTask.isRecommended)) {
            return true;
          }
        } else { // attempted outside filter - rebalance pickups can never be attempted outside of recommendation
          return false;
        }
      }
    }
    if (typeFilters.includes(PURE_PICKUP)) {
      if (isPurePickup) {
        if (statusFilter === statusFilterTypes.ALL) {
          return true;
        } else if (statusFilter === statusFilterTypes.ATTEMPTED_OUTSIDE) {
          return !!runTask.attemptReason;
        } else if (statusFilter === statusFilterTypes.COMPLETED_OUTSIDE) {
          return true; // anything else that isn't attempted would be completed
        }
      }
    }
    return false;
  };

  const findTimeDiff = (first, second) => {
    const start = new Date(first);
    const end = new Date(second);
    let diffMs = end - start;
    let timeStatus = 'late';
    if (start > end) {
      diffMs = start - end;
      timeStatus = 'early';
    }
    const diffHrs = Math.floor((diffMs % 86400000) / 3600000);
    const diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000);

    if (diffMins === 0 && diffHrs === 0) {
      return 'On time';
    } else if (diffMins === 0) {
      return `${diffHrs} hour(s) ${timeStatus}`;
    } else if (diffHrs === 0) {
      return `${diffMins} min(s) ${timeStatus}`;
    } else {
      return `${diffHrs} hour(s), ${diffMins} min(s) ${timeStatus}`;
    }
  };

  const countTasks = (statusFilter, typeFilters) => {
    let count = 0;
    run.tasks.forEach((runTask) => {
      if (showTask(runTask, statusFilter, typeFilters)) {
        count++;
      }
    });
    return count;
  };

  const getCount = (statusFilter) => {
    const count = countTasks(statusFilter, typeFilters);
    if (statusFilter === statusFilterTypes.COMPLETED && typeFilters.includes(RunTaskTypes.PICKUP)) {
      // two completed rebalance tasks = 1 rebalance
      const rebalances = countTasks(statusFilter, [RunTaskTypes.PICKUP]);
      return count - (rebalances / 2);
    }
    return count;
  };

  return (
    <Dialog
      maxWidth="lg"
      open={open}
      onClose={handleClose}
    >
      <DialogTitle style={styles.title}>
        <Typography style={styles.header}>
          {`${dayjs(run.startDate.date).tz(tz).format(localeFullDateFormat)}`}
          {run.startDate.hasTime && ` ${dayjs(run.startDate.date).tz(tz).format(localeTimeFormat)}`}
        </Typography>
        <Typography style={styles.subHeader}>
          {run.name}
        </Typography>
        {run.wranglers.map((wrangler) => (
          <Typography style={styles.subHeader} key={wrangler._id}>
            {`${wrangler.firstName} ${wrangler.lastName}`}
          </Typography>
        ))}
      </DialogTitle>
      <DialogContent style={styles.content}>
        <div style={styles.flexRow}>
          <div style={styles.flexRow}>
            <CheckCircleIcon style={styles.timeIcon} />
            <Typography style={styles.timeDisplay}>
              {`Started at ${dayjs(get(run, 'startedAt')).tz(tz).format(localeTimeFormat)}`}
              <br />
              {findTimeDiff(run.startDate.date, run.startedAt)}
            </Typography>
          </div>
          <Typography style={styles.timeDisplay}>
            {`Closed at ${dayjs(get(run, 'closedAt')).tz(tz).format(localeTimeFormat)}`}
          </Typography>
          <Typography style={styles.timeDisplay}>
            {Math.round(get(run, 'metrics.adherenceScore', 0) * 100)}
            %
            <br />
            Score
          </Typography>
        </div>
        <div style={styles.progressContainer}>
          <RunTaskProgress
            label="Pick up"
            recommended={get(run, 'metrics.pickupsRecommended', 0)}
            completed={get(run, 'metrics.pickupsCompleted', 0)}
            checked={typeFilters.includes(PURE_PICKUP)}
            onChange={() => changeTypeFilter(PURE_PICKUP)}
          />
          <RunTaskProgress
            label="Drop off"
            recommended={get(run, 'metrics.deploysRecommended', 0)}
            completed={get(run, 'metrics.deploysCompleted', 0)}
            checked={typeFilters.includes(RunTaskTypes.DEPLOY)}
            onChange={() => changeTypeFilter(RunTaskTypes.DEPLOY)}
          />
          <RunTaskProgress
            label="Rebalance"
            recommended={get(run, 'metrics.rebalancesRecommended', 0)}
            completed={get(run, 'metrics.rebalancesCompleted', 0)}
            checked={typeFilters.includes(RunTaskTypes.PICKUP)}
            onChange={() => changeTypeFilter(RunTaskTypes.PICKUP)}
          />
        </div>
        <FormControl
          fullWidth
          variant="outlined"
          style={styles.selectContainer}
        >
          <InputLabel style={styles.label}>Task status</InputLabel>
          <Select
            value={statusFilter}
            label="Task status"
            onChange={(event) => setStatusFilter(event.target.value)}
            style={styles.select}
          >
            {Object.keys(statusFilterTypes).map((key) => (
              <MenuItem key={key} value={statusFilterTypes[key]}>{`${statusFilterTypes[key]} (${getCount(statusFilterTypes[key])})`}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <div style={styles.runTaskContainer}>
          {run.warehouse && (
            <RunTask
              runTask={{
                isWarehouse: true,
                location: run.warehouse,
                timeDiff: findTimeDiff(run.startDate.date, run.startedAt),
                completedAt: run.startedAt,
              }}
              tz={tz}
            />
          )}
          {run.tasks.map((runTask) => (showTask(runTask, statusFilter, typeFilters) ? (
            <RunTask
              key={runTask._id}
              runTask={runTask}
              tz={tz}
            />
          ) : ''))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

BaseRunTaskModal.defaultProps = {};
BaseRunTaskModal.propTypes = {
  open: PropTypes.bool.isRequired,
  tz: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
  run: PropTypes.shape({
    _id: PropTypes.string,
  }).isRequired,
};

const RunTaskModal = withRouter(BaseRunTaskModal);
export { BaseRunTaskModal, RunTaskModal };
